// @ts-nocheck

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { InvestorProfile } from '../../constants';
import Text from '@/components/Text';
import Button, { ButtonType } from '@/components/Button';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRotateLeft,
  faInfoCircle,
  faChevronDown,
  faBrain,
  faSearch,
  faChartPie,
  faWallet,
  faUserTie,
  faRocket,
  faArrowTrendUp,
  faArrowTrendDown,
  faUsers,
  faGem,
  faArrowRight,
  faPlay,
  faChartLine,
  faMoneyBillTransfer,
  faShuffle,
  faBolt,
  faEye,
  faCrosshairs,
  faShareNodes,
} from '@fortawesome/pro-thin-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import AssetListItem, { Asset } from '@/common/AssetListItem';
import OrderModal from '@/common/OrderModal';
import { useQuery } from '@tanstack/react-query';
import AssetService from '@/lib/Asset';
import { OrderSide } from '@/lib/Order/types';
import { fetchPersonalityRecommendations, fetchFundDetails } from '@/utils/supabase';
import { useRouter } from 'next/router';
import Analytics from '@/system/Analytics';
import { DiscoverEvents } from '@/system/Analytics/events/DiscoverEvents';
import { YourTypeEvents } from '@/system/Analytics/events/YourTypeEvents';
import Modal from '@/components/Modal';
import useTradingAccount from '@/hooks/useTradingAccount';
import useCurrentUser from '@/hooks/useCurrentUser';
import PortfolioInvestModal, { PortfolioStock } from '../PortfolioInvestModal';
import YourTypeGetAppModal from '../YourTypeGetAppModal';
import ShareModal from '../ShareModal';

import KeyStats from '@/pages/asset/KeyStats';
import About from '@/pages/asset/About';
import News from '@/pages/asset/News';
import IndividualAssetGraph from '@/pages/asset/IndividualAssetGraph';
import AssetImage from '@/components/AssetImage';
import Image from 'next/image';

import bloomieAnalytical from '@/assets/bloomie_analytical.png';
import bloomieBalanced from '@/assets/bloomie_balanced.png';
import bloomieCollaborative from '@/assets/bloomie_collaborative.png';
import bloomieContrarian from '@/assets/bloomie_contrarian.png';
import bloomieStrategic from '@/assets/bloomie_strategic.png';
import bloomieVisionary from '@/assets/bloomie_visionary.png';

const BLOOMIE_IMAGES: Record<string, typeof bloomieAnalytical> = {
  ANALYTICAL: bloomieAnalytical,
  BALANCED: bloomieBalanced,
  COLLABORATIVE: bloomieCollaborative,
  CONTRARIAN: bloomieContrarian,
  STRATEGIC: bloomieStrategic,
  VISIONARY: bloomieVisionary,
};

const AXIS_EXPLANATIONS: Record<string, string> = {
  Innovation: "Propensity for disruptive technologies and future-focused growth.",
  Fundamentals: "Focus on financial health, balance sheets, and proven business models.",
  Community: "Emphasis on social impact, ESG, and collaborative investment wisdom.",
  Strategy: "Tactical approach based on market momentum and data-driven signals.",
};

interface YourTypeResultsProps {
  profile: InvestorProfile;
  answers: Record<number, 'A' | 'B' | 'C' | 'D' | 'E'> | null;
  prefetchData?: any;
  onRestart: () => void;
  onReplayWrapped?: () => void;
}

const PIE_COLORS = [
  '#3b82f6', // Blue
  '#2dd4bf', // Teal
  '#4ade80', // Green
  '#facc15', // Yellow
  '#fb923c', // Orange
  '#f87171', // Red
  '#a78bfa', // Purple
  '#38bdf8', // Sky
  '#818cf8'  // Indigo
];

const CustomPieTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-black/90 backdrop-blur-md border border-white/10 p-3 rounded-xl shadow-2xl">
        <div className="flex flex-col gap-1">
          <Text size="sm" className="text-teal-200 font-bold uppercase tracking-wider">
            {payload[0].name}
          </Text>
          <div className="flex items-baseline gap-2">
            <Text size="xl" className="font-bold text-white">{(payload[0].value).toFixed(2)}%</Text>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const CustomRadarTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-black/80 backdrop-blur-md border border-teal-200/20 p-4 rounded-2xl shadow-2xl animate-in fade-in zoom-in duration-200">
        <div className="flex flex-col gap-2">
          {payload[0].payload.subject && (
            <Text size="xs" className="text-teal-200 font-bold uppercase tracking-wider">
              {payload[0].payload.subject}
            </Text>
          )}
          <div className="flex items-baseline gap-2">
            <Text size="2xl" className="font-bold text-white">{payload[0].value}</Text>
            <Text size="xs" className="text-white/40">/ 100</Text>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const StockDetailModal: React.FC<{
  symbol: string;
  onClose: () => void;
  requireTradingOrShowGetAppModal: (source: string) => boolean;
}> = ({ symbol, onClose, requireTradingOrShowGetAppModal }) => {
  const [latestPrice, setLatestPrice] = useState<number>(0);
  const { data: info, isLoading } = useQuery({
    queryKey: ['assetInfo', symbol],
    queryFn: () => AssetService.getAssetInfo(symbol),
    enabled: !!symbol,
  });
  const { data: tradingAccount } = useTradingAccount();
  const [orderModal, setOrderModal] = useState<{ orderSide: OrderSide; showModal: boolean }>({
    orderSide: OrderSide.BUY,
    showModal: false,
  });

  const onBuy = () => {
    if (!requireTradingOrShowGetAppModal('stock_detail_buy')) return;
    setOrderModal({ orderSide: OrderSide.BUY, showModal: true });
  };

  const closeOrderModal = () => {
    setOrderModal({ ...orderModal, showModal: false });
  };

  // Prevent parent modal from closing while OrderModal is open
  const handleClose = () => {
    if (orderModal.showModal) return;
    onClose();
  };

  return (
    <>
      <Modal open={!!symbol && !orderModal.showModal} closeModal={handleClose}>
        <div className="bg-sharp-darkglass w-full max-w-2xl rounded-[2.5rem] overflow-hidden flex flex-col max-h-[90vh] shadow-2xl border border-white/10">
          <div className="p-6 md:p-8 overflow-y-auto scrollbar-apple">
            <div className="flex justify-between items-start mb-8">
              <div className="flex items-center gap-6">
                <AssetImage alt={symbol} src={info?.image || ''} className="size-16 md:size-20 rounded-2xl shadow-xl" />
                <div className="flex flex-col gap-1">
                  <Text size="2xl" className="font-bold text-white uppercase leading-none">{info?.name || symbol}</Text>
                  <Text size="sm" className="text-teal-200 font-bold uppercase tracking-widest opacity-60">{symbol}</Text>
                </div>
              </div>
              <button
                onClick={onClose}
                className="size-10 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:bg-white/10 hover:text-white transition-all"
              >
                <FontAwesomeIcon icon={faRotateLeft as IconProp} className="size-4 rotate-45" />
              </button>
            </div>

            <div className="flex flex-col gap-8">
              <div className="bg-black/20 rounded-[2rem] p-6 border border-white/5">
                <IndividualAssetGraph symbol={symbol} setLatestPrice={setLatestPrice} />
              </div>

              <div className="grid gap-6">
                {info && <About assetInfo={info} className="bg-white/5 p-6 rounded-[2rem] border border-white/5" />}
                <KeyStats isEtf={!!info?.isEtf} symbol={symbol} className="bg-white/5 p-6 rounded-[2rem] border border-white/5" />
                <News symbol={symbol} className="bg-white/5 p-6 rounded-[2rem] border border-white/5" />
              </div>
            </div>
          </div>

          <div className="p-6 bg-black/40 border-t border-white/5 flex gap-4">
            <Button onClick={onClose} type={ButtonType.Secondary} className="flex-1 rounded-2xl h-14 font-bold uppercase tracking-widest">
              Close
            </Button>
            <Button
              type={ButtonType.Primary}
              className="flex-1 rounded-2xl h-14 font-bold uppercase tracking-widest"
              onClick={onBuy}
            >
              Buy stock
            </Button>
          </div>
        </div>
      </Modal>
      <OrderModal
        open={orderModal.showModal}
        closeModal={closeOrderModal}
        orderSide={orderModal.orderSide}
        buyingPower={Number(tradingAccount?.buying_power || 0)}
        asset={{
          name: info?.name || symbol,
          symbol,
          availableToSell: 0,
          sharesOwned: 0,
          latestPrice,
        }}
      />
    </>
  );
};

const HoldingItem: React.FC<{ symbol: string; quote: any; mini?: boolean; onSelect?: (symbol: string) => void }> = ({ symbol, quote, mini, onSelect }) => {
  const { data: info, isLoading } = useQuery({
    queryKey: ['assetInfo', symbol],
    queryFn: () => AssetService.getAssetInfo(symbol),
    enabled: !!symbol && symbol !== 'undefined',
    staleTime: 10 * 60 * 1000,
  });

  const asset: Asset = useMemo(() => ({
    symbol,
    name: info?.name || symbol,
    image: info?.image || '',
    latestPrice: quote?.realLatestPrice || 0,
    changePercent: (quote?.changePercent || 0),
    change: quote?.change || 0,
  }), [symbol, info, quote]);

  if (isLoading) return <div className={mini ? "h-12 w-32 animate-pulse bg-white/5 rounded-xl" : "h-20 w-full animate-pulse bg-white/5 rounded-2xl mb-2"} />;

  if (mini) {
    return (
      <button
        onClick={() => {
          Analytics.track(DiscoverEvents.CLICK_CATEGORY_ASSET, { asset });
          if (onSelect) onSelect(symbol);
        }}
        className="flex items-center gap-3 p-2 pr-4 bg-white/5 hover:bg-teal-200 hover:text-black rounded-xl border border-white/10 transition-all group/mini overflow-hidden min-w-[140px]"
      >
        <div className="flex-none size-8 rounded-lg bg-black/20 flex items-center justify-center font-bold text-xs group-hover/mini:bg-black/10">
          {symbol}
        </div>
        <div className="flex flex-col items-start min-w-0">
          <Text size="sm" className="font-bold truncate w-full inherit">{symbol}</Text>
          <Text size="sm" className={asset.changePercent >= 0 ? "text-green-400 group-hover/mini:text-black/60" : "text-red-400 group-hover/mini:text-black/60"}>
            {asset.changePercent >= 0 ? '+' : ''}{asset.changePercent.toFixed(2)}%
          </Text>
        </div>
      </button>
    );
  }

  return (
    <AssetListItem
      asset={asset}
      onClick={(s) => {
        Analytics.track(DiscoverEvents.CLICK_CATEGORY_ASSET, { asset });
        if (onSelect) onSelect(s);
      }}
      className="bg-transparent hover:bg-white/5 px-6"
    />
  );
};

const LegendItem: React.FC<{ symbol: string; weight: number; color: string }> = ({ symbol, weight, color }) => {
  const { data: info } = useQuery({
    queryKey: ['assetInfo', symbol],
    queryFn: () => AssetService.getAssetInfo(symbol),
    enabled: !!symbol && symbol !== 'Others' && symbol !== 'undefined',
    staleTime: 10 * 60 * 1000,
  });

  return (
    <div className="flex items-center gap-2 group/legend overflow-hidden">
      <div className="size-1 rounded-full flex-none" style={{ backgroundColor: color }} />
      <div className="size-6 rounded-lg bg-black/40 border border-white/5 flex items-center justify-center overflow-hidden flex-none group-hover/legend:border-teal-200/30 transition-colors">
        {info?.image ? (
          <img src={info.image} alt="" className="w-full h-full object-cover" />
        ) : symbol === 'Others' ? (
          <FontAwesomeIcon icon={faGem as IconProp} className="size-2 text-white/20" />
        ) : (
          <Text size="xs" className="font-black text-white/20 uppercase">{symbol?.slice(0, 2)}</Text>
        )}
      </div>
      <div className="flex flex-col min-w-0">
        <Text size="xs" className="font-bold text-white/90 uppercase tracking-tight truncate leading-none mb-0.5">{symbol}</Text>
        <Text className="text-teal-200/40 text-[9px] font-black leading-none">{weight.toFixed(1)}%</Text>
      </div>
    </div>
  );
};

// ── Match Gauge — SVG speedometer with needle + semantic label below ──
const MATCH_TIERS = [
  { label: 'Top Match', color: '#2dd4bf', darkColor: '#0f766e' },   // teal-400 / teal-700
  { label: 'Strong', color: '#34d399', darkColor: '#047857' },   // emerald-400 / emerald-700
  { label: 'Great Fit', color: '#4ade80', darkColor: '#15803d' },   // green-400 / green-700
  { label: 'Solid', color: '#a3e635', darkColor: '#4d7c0f' },   // lime-400 / lime-700
  { label: 'Good Fit', color: '#86efac', darkColor: '#166534' },   // green-300 / green-800
];

const MatchGauge: React.FC<{ score: number; minScore: number; maxScore: number; rank: number; total: number; size?: 'sm' | 'md'; inverted?: boolean }> = ({ score, minScore, maxScore, rank, total, size = 'sm', inverted = false }) => {
  const range = maxScore - minScore;
  const normalized = range > 0 ? (score - minScore) / range : 1;
  const fillPercent = 0.60 + normalized * 0.35;

  const tierIdx = Math.min(rank, MATCH_TIERS.length - 1);
  const tier = MATCH_TIERS[tierIdx];

  // When inverted (selected button with light bg), use dark colors
  const arcColor = inverted ? tier.darkColor : tier.color;
  const needleColor = inverted ? '#1a1a1a' : 'white';
  const hubColor = inverted ? '#1a1a1a' : 'white';
  const bgArcColor = inverted ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.08)';
  const labelColor = inverted ? tier.darkColor : tier.color;

  const isSm = size === 'sm';
  const radius = isSm ? 18 : 26;
  const sw = isSm ? 4 : 5;
  const w = (radius + sw) * 2 + 4;
  const cy = radius + sw + 2;
  const cx = w / 2;
  const svgH = cy + 2;

  // Semi-circle arc path (left → right)
  const startX = cx - radius;
  const endX = cx + radius;
  const arcPath = `M ${startX} ${cy} A ${radius} ${radius} 0 0 1 ${endX} ${cy}`;
  const circumference = Math.PI * radius;
  const dashOffset = circumference * (1 - fillPercent);

  // Needle: angle goes from π (left, 0%) to 0 (right, 100%)
  const needleAngle = Math.PI * (1 - fillPercent);
  const needleLen = radius - sw - 1;
  const needleTipX = cx + needleLen * Math.cos(needleAngle);
  const needleTipY = cy - needleLen * Math.sin(needleAngle);
  const hubR = isSm ? 2.5 : 3;

  return (
    <div className="flex flex-col items-center gap-0.5">
      <svg width={w} height={svgH} viewBox={`0 0 ${w} ${svgH}`}>
        {/* Background arc */}
        <path d={arcPath} fill="none" stroke={bgArcColor} strokeWidth={sw} strokeLinecap="round" />
        {/* Filled arc */}
        <path
          d={arcPath}
          fill="none"
          stroke={arcColor}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          className="transition-all duration-1000 ease-out"
        />
        {/* Needle */}
        <line
          x1={cx}
          y1={cy}
          x2={needleTipX}
          y2={needleTipY}
          stroke={needleColor}
          strokeWidth={isSm ? 1.5 : 2}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
          style={{ filter: `drop-shadow(0 0 2px ${arcColor})` }}
        />
        {/* Center hub dot */}
        <circle cx={cx} cy={cy} r={hubR} fill={hubColor} />
      </svg>
      {/* Label below */}
      <span
        className="font-black uppercase leading-none"
        style={{
          color: labelColor,
          fontSize: isSm ? 8 : 10,
          letterSpacing: '0.06em',
        }}
      >
        {tier.label}
      </span>
    </div>
  );
};

const BlueprintStockItem: React.FC<{ stock: any; color: string; onSelect: (ticker: string) => void }> = ({ stock, color, onSelect }) => {
  const { data: info } = useQuery({
    queryKey: ['assetInfo', stock.ticker],
    queryFn: () => AssetService.getAssetInfo(stock.ticker),
    enabled: !!stock.ticker,
    staleTime: 10 * 60 * 1000,
  });

  return (
    <button
      onClick={() => onSelect(stock.ticker)}
      className="flex w-full items-center justify-between p-3 md:p-4 hover:bg-white/5 transition-all group"
    >
      <div className="flex items-center gap-3 md:gap-4">
        <div
          className="size-2.5 md:size-3 rounded-sm shadow-sm flex-none"
          style={{ backgroundColor: color }}
        />
        <Text className="font-bold text-white/90 text-sm md:text-base group-hover:text-teal-200 transition-colors">
          {info?.name || stock.ticker}
        </Text>
      </div>
      <div className="flex items-center gap-4">
        <Text className="text-white/80 font-medium text-sm md:text-base">
          {stock.percentage.toFixed(1)}%
        </Text>
      </div>
    </button>
  );
};


// Helper: format large numbers (e.g. 6897676080637 → "$6.9T", 86892434532 → "$86.9B")
function formatLargeNumber(value: number | null | undefined): string {
  if (value == null) return '—';
  const abs = Math.abs(value);
  if (abs >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  return `$${value.toLocaleString()}`;
}

// Performance chart tooltip
const CustomChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-black/90 backdrop-blur-md border border-white/10 p-3 rounded-xl shadow-2xl">
        <Text size="xs" className="text-white/40 font-bold mb-1">{label}</Text>
        <Text size="lg" className={`font-bold ${payload[0].value >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {payload[0].value >= 0 ? '+' : ''}{payload[0].value.toFixed(2)}%
        </Text>
      </div>
    );
  }
  return null;
};

// Fund Performance Chart Component
const FundPerformanceChart: React.FC<{
  returnBars1Y: any;
  returnBars5Y: any;
}> = ({ returnBars1Y, returnBars5Y }) => {
  const [timeframe, setTimeframe] = useState<'1Y' | '5Y'>('1Y');

  const chartData = useMemo(() => {
    const source = timeframe === '1Y' ? returnBars1Y : returnBars5Y;
    if (!source) return [];
    // Handle nested bars structure: { bars: [{ bars: [...] }] } or flat { bars: [...] }
    const rawBars = source.bars;
    if (!rawBars || rawBars.length === 0) return [];
    // Check if it's nested (first element has its own .bars array)
    const actualBars = rawBars[0]?.bars ? rawBars[0].bars : rawBars;
    return actualBars.map((b: any) => ({
      date: b.date,
      // Format date label: "Jan 27" style
      label: new Date(b.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      percent: b.percent,
    }));
  }, [timeframe, returnBars1Y, returnBars5Y]);

  const latestReturn = chartData.length > 0 ? chartData[chartData.length - 1].percent : null;

  if (chartData.length === 0) return null;

  return (
    <div className="flex flex-col gap-4 pt-4 border-t border-white/5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faChartLine as IconProp} className="size-4 text-teal-200/60" />
          <Text size="sm" className="text-white/40 uppercase tracking-widest font-bold">Top 15 Performance</Text>
        </div>
        <div className="flex gap-1 bg-white/5 rounded-xl p-1">
          {(['1Y', '5Y'] as const).map(tf => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={`px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${timeframe === tf
                ? 'bg-teal-200 text-black'
                : 'text-white/40 hover:text-white/60'
                }`}
            >
              {tf}
            </button>
          ))}
        </div>
      </div>
      {latestReturn !== null && (
        <div className="flex items-baseline gap-2">
          <Text size="2xl" className={`font-bold ${latestReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {latestReturn >= 0 ? '+' : ''}{latestReturn.toFixed(2)}%
          </Text>
          <Text size="sm" className="text-white/30 uppercase tracking-widest font-bold">{timeframe === '1Y' ? '1 Year' : '5 Year'} Return</Text>
        </div>
      )}
      <div className="bg-black/20 rounded-3xl p-4 border border-white/5">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <defs>
              <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#99f6e4" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#99f6e4" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
            <XAxis
              dataKey="label"
              tick={{ fill: '#ffffff30', fontSize: 10 }}
              axisLine={{ stroke: '#ffffff10' }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: '#ffffff30', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${v}%`}
              width={45}
            />
            <Tooltip content={<CustomChartTooltip />} />
            <Area
              type="monotone"
              dataKey="percent"
              stroke="#99f6e4"
              strokeWidth={2}
              fill="url(#chartGradient)"
              dot={false}
              activeDot={{ r: 4, fill: '#99f6e4', stroke: '#000', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Position Activity Summary Component
const PositionActivityCard: React.FC<{ details: any }> = ({ details }) => {
  if (!details) return null;

  const items = [
    { label: 'New', value: details.new_positions, change: details.new_positions_change, color: 'text-green-400' },
    { label: 'Closed', value: details.closed_positions, change: details.closed_positions_change, color: 'text-red-400' },
    { label: 'Increased', value: details.increased_positions, change: details.increased_positions_change, color: 'text-teal-200' },
    { label: 'Reduced', value: details.reduced_positions, change: details.reduced_positions_change, color: 'text-orange-400' },
  ];

  return (
    <div className="flex flex-col gap-3 pt-4 border-t border-white/5">
      <div className="flex items-center gap-3">
        <FontAwesomeIcon icon={faShuffle as IconProp} className="size-4 text-teal-200/60" />
        <Text size="sm" className="text-white/40 uppercase tracking-widest font-bold">Position Activity</Text>
        <Text size="xs" className="text-white/20 ml-auto">Q{details.reporting_quarter} {details.reporting_year}</Text>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {items.map(item => (
          <div key={item.label} className="bg-white/5 p-3 rounded-2xl border border-white/5 flex flex-col gap-1 hover:bg-white/10 transition-colors">
            <Text className="text-[10px] text-secondary uppercase tracking-wider font-bold">{item.label}</Text>
            <div className="flex items-baseline gap-1.5">
              <Text className={`text-lg font-bold ${item.color}`}>{item.value ?? '—'}</Text>
              {item.change != null && item.change !== 0 && (
                <Text className={`text-[10px] font-bold ${item.change > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {item.change > 0 ? '+' : ''}{item.change}
                </Text>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Deep Analysis Phase Definitions
const ANALYSIS_PHASES = [
  {
    key: 'strength',
    label: 'Superpower',
    icon: faBolt,
    accentHex: '#99f6e4',
    gradient: 'from-teal-500/20 via-teal-800/5 to-transparent',
    border: 'border-teal-400/30',
    text: 'text-teal-200',
    glow: 'shadow-[0_0_40px_rgba(153,246,228,0.15)]',
    bgPulse: 'bg-teal-400',
    ringColor: 'ring-teal-400/30',
  },
  {
    key: 'blindspot',
    label: 'Blind Spot',
    icon: faEye,
    accentHex: '#fbbf24',
    gradient: 'from-amber-500/20 via-amber-800/5 to-transparent',
    border: 'border-amber-400/30',
    text: 'text-amber-300',
    glow: 'shadow-[0_0_40px_rgba(251,191,36,0.15)]',
    bgPulse: 'bg-amber-400',
    ringColor: 'ring-amber-400/30',
  },
  {
    key: 'strategy',
    label: 'Playbook',
    icon: faCrosshairs,
    accentHex: '#818cf8',
    gradient: 'from-indigo-500/20 via-indigo-800/5 to-transparent',
    border: 'border-indigo-400/30',
    text: 'text-indigo-300',
    glow: 'shadow-[0_0_40px_rgba(129,140,248,0.15)]',
    bgPulse: 'bg-indigo-400',
    ringColor: 'ring-indigo-400/30',
  },
];

// Deep Analysis — Interactive Triptych
const DeepAnalysisSection: React.FC<{ analysis: { title: string; content: string }[] }> = ({ analysis }) => {
  const [activePhase, setActivePhase] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  // Intersection observer to trigger animation on scroll
  useEffect(() => {
    if (!sectionRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasBeenVisible) {
          setHasBeenVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [hasBeenVisible]);

  // Auto-cycle through phases
  useEffect(() => {
    if (!isAutoPlaying || !hasBeenVisible) return;
    const PHASE_DURATION = 6000; // 6 seconds per phase
    const TICK = 30;
    let elapsed = 0;
    const interval = setInterval(() => {
      elapsed += TICK;
      setProgress((elapsed / PHASE_DURATION) * 100);
      if (elapsed >= PHASE_DURATION) {
        elapsed = 0;
        setActivePhase(prev => (prev + 1) % 3);
        setProgress(0);
      }
    }, TICK);
    return () => clearInterval(interval);
  }, [isAutoPlaying, activePhase, hasBeenVisible]);

  const handlePhaseClick = (idx: number) => {
    setActivePhase(idx);
    setIsAutoPlaying(false);
    setProgress(0);
  };

  const phase = ANALYSIS_PHASES[activePhase];
  const section = analysis[activePhase];

  // Parse the title to extract the keyword after the colon
  const titleParts = section?.title?.split(':') || [];
  const titlePrefix = titleParts[0]?.trim() || '';
  const titleKeyword = titleParts[1]?.trim() || section?.title || '';

  return (
    <div
      ref={sectionRef}
      className={`
        relative rounded-[2rem] overflow-hidden transition-all duration-700
        ${hasBeenVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
      `}
    >
      {/* Animated Background Gradient */}
      <div
        className={`absolute inset-0 bg-gradient-to-br ${phase.gradient} transition-all duration-700 pointer-events-none`}
      />
      <div className="absolute inset-0 bg-black/20 pointer-events-none" />

      <div className="relative z-10 px-4 md:px-6 py-6 md:py-8 flex flex-col gap-8">
        {/* Phase Navigation — Connected Dots */}
        <div className="flex items-center justify-center gap-0">
          {ANALYSIS_PHASES.map((p, idx) => (
            <React.Fragment key={p.key}>
              {/* Dot */}
              <button
                onClick={() => handlePhaseClick(idx)}
                className="relative flex flex-col items-center gap-3 group z-10"
              >
                <div className={`
                  relative size-12 md:size-14 rounded-2xl flex items-center justify-center transition-all duration-500
                  ${idx === activePhase
                    ? `bg-gradient-to-br ${p.gradient} border-2 ${p.border} ring-4 ${p.ringColor} scale-110`
                    : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 scale-100'
                  }
                `}>
                  <FontAwesomeIcon
                    icon={p.icon as IconProp}
                    className={`size-5 md:size-6 transition-all duration-500 ${idx === activePhase ? p.text : 'text-white/30 group-hover:text-white/50'}`}
                  />
                  {/* Active indicator pulse */}
                  {idx === activePhase && (
                    <div className={`absolute -bottom-1 size-2 rounded-full ${p.bgPulse} animate-pulse`} />
                  )}
                </div>
                <Text
                  size="xs"
                  className={`
                    font-black uppercase tracking-[0.15em] transition-all duration-500 whitespace-nowrap
                    ${idx === activePhase ? `${p.text} opacity-100` : 'text-white/25 opacity-70 group-hover:opacity-100 group-hover:text-white/40'}
                  `}
                >
                  {p.label}
                </Text>
              </button>

              {/* Connector Line */}
              {idx < 2 && (
                <div className="relative w-12 md:w-20 h-px mx-1 md:mx-2 -mt-6">
                  <div className="absolute inset-0 bg-white/10 rounded-full" />
                  {/* Animated fill — fills when we've passed this connector */}
                  <div
                    className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
                    style={{
                      width: activePhase > idx ? '100%' : activePhase === idx ? `${progress}%` : '0%',
                      backgroundColor: ANALYSIS_PHASES[Math.min(idx + 1, 2)].accentHex,
                      opacity: 0.6,
                    }}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Content Area with Animated Transition */}
        <div className="relative min-h-[180px] md:min-h-[160px]">
          {analysis.map((section, idx) => {
            const p = ANALYSIS_PHASES[idx];
            const parts = section.title?.split(':') || [];
            const prefix = parts[0]?.trim() || '';
            const keyword = parts[1]?.trim() || section.title || '';
            const isActive = idx === activePhase;

            return (
              <div
                key={idx}
                className={`
                  transition-all duration-500 ease-out
                  ${isActive
                    ? 'opacity-100 translate-y-0 relative'
                    : 'opacity-0 translate-y-4 absolute inset-0 pointer-events-none'
                  }
                `}
              >
                <div className="flex flex-col gap-4">
                  {/* Title with accent keyword */}
                  <div className="flex flex-col gap-1">
                    <Text size="xs" className={`${p.text} uppercase tracking-[0.2em] font-black opacity-60`}>
                      {prefix}
                    </Text>
                    <h3 className={`text-2xl md:text-3xl font-black uppercase tracking-tight ${p.text}`}>
                      {keyword}
                    </h3>
                  </div>

                  {/* Divider */}
                  <div className="h-px w-16 rounded-full" style={{ backgroundColor: p.accentHex, opacity: 0.4 }} />

                  {/* Content */}
                  <span
                    className="text-base md:text-lg text-white/70 leading-relaxed font-medium"
                    dangerouslySetInnerHTML={{ __html: section.content }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Progress Bar */}
        {isAutoPlaying && (
          <div className="h-0.5 w-full bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-[width] duration-100 ease-linear"
              style={{
                width: `${((activePhase * 100) + progress) / 3}%`,
                backgroundColor: phase.accentHex,
                opacity: 0.5,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ── "It's a Match!" Reveal Card with Scroll-Triggered Animation ──
const ManagerMatchReveal: React.FC<{
  rec: any;
  idx: number;
  profile: InvestorProfile;
  activeHoldings: any;
  blueprintAllStocks: any[];
  openPortfolioInvestModal: (stocks: any[], name: string, desc: string, image?: any) => void;
  requireTradingOrShowGetAppModal: (source: string) => boolean;
}> = ({ rec, idx, profile, activeHoldings, blueprintAllStocks, openPortfolioInvestModal, requireTradingOrShowGetAppModal }) => {
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!cardRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !revealed) {
          setRevealed(true);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [revealed]);

  return (
    <div
      ref={cardRef}
      className={`
        group relative overflow-hidden rounded-[2.5rem] border shadow-2xl
        transition-all duration-700 ease-out
        ${revealed
          ? 'opacity-100 translate-y-0 border-emerald-400/20 shadow-emerald-500/10 hover:shadow-emerald-500/20 hover:border-emerald-400/30'
          : 'opacity-0 translate-y-12 border-transparent shadow-transparent'
        }
      `}
      style={{
        background: 'linear-gradient(180deg, rgba(16,185,129,0.25) 0%, rgba(6,78,59,0.18) 40%, rgba(0,0,0,0.5) 100%)',
      }}
    >
      {/* ── Animated Sparkle / Star Decorations (only visible after reveal) ── */}
      <div className={`absolute inset-0 overflow-hidden pointer-events-none select-none transition-opacity duration-1000 ${revealed ? 'opacity-100' : 'opacity-0'}`}>
        <span className="absolute top-[8%] left-[18%] text-2xl text-white/25 animate-pulse" style={{ animationDuration: '2s' }}>✦</span>
        <span className="absolute top-[12%] right-[22%] text-lg text-emerald-300/20 animate-pulse" style={{ animationDuration: '3s', animationDelay: '0.6s' }}>✧</span>
        <span className="absolute top-[5%] right-[12%] text-xl text-white/15 animate-pulse" style={{ animationDuration: '2.5s', animationDelay: '1.2s' }}>✦</span>
        <span className="absolute top-[22%] left-[8%] text-sm text-emerald-200/15 animate-pulse" style={{ animationDuration: '3.5s', animationDelay: '1.8s' }}>✧</span>
        <span className="absolute bottom-[35%] right-[8%] text-sm text-white/10 animate-pulse" style={{ animationDuration: '2s', animationDelay: '2.4s' }}>✦</span>
        <span className="absolute top-[30%] left-[50%] text-xs text-emerald-300/10 animate-pulse" style={{ animationDuration: '4s', animationDelay: '0.3s' }}>✧</span>
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-96 h-96 bg-emerald-900/20 rounded-full blur-3xl" />
        <div className="absolute top-1/4 -left-16 w-48 h-48 bg-teal-400/5 rounded-full blur-2xl" />
        <div className="absolute top-1/4 -right-16 w-48 h-48 bg-teal-400/5 rounded-full blur-2xl" />
      </div>

      <div className="relative z-10 px-6 md:px-10 py-10 md:py-14 flex flex-col items-center gap-7">
        {/* ── Overlapping Avatar Circles — slide in from left & right ── */}
        <div className="flex items-center justify-center mb-1">
          {/* User's bloomie avatar — slides in from left */}
          <div
            className={`
              size-[6.5rem] md:size-[7.5rem] rounded-full bg-gradient-to-br from-teal-200/20 via-emerald-500/10 to-transparent
              border-[3px] border-white/25 shadow-2xl shadow-emerald-500/20 overflow-hidden
              flex items-center justify-center z-10 group-hover:scale-105
              transition-all ease-out
              ${revealed ? 'opacity-100 translate-x-0 duration-700' : 'opacity-0 -translate-x-16 duration-0'}
            `}
            style={{ transitionDelay: revealed ? '200ms' : '0ms' }}
          >
            {BLOOMIE_IMAGES[profile.id] && (
              <Image
                src={BLOOMIE_IMAGES[profile.id]}
                alt={profile.name}
                width={110}
                height={110}
                className="size-[5.5rem] md:size-[6.5rem] object-contain drop-shadow-lg"
              />
            )}
          </div>
          {/* Manager photo — slides in from right */}
          <div
            className={`
              -ml-7 size-[6.5rem] md:size-[7.5rem] rounded-full bg-black/60
              border-[3px] border-white/25 shadow-2xl shadow-emerald-500/20 overflow-hidden
              flex items-center justify-center group-hover:scale-105
              transition-all ease-out
              ${revealed ? 'opacity-100 translate-x-0 duration-700' : 'opacity-0 translate-x-16 duration-0'}
            `}
            style={{ transitionDelay: revealed ? '200ms' : '0ms' }}
          >
            {rec.manager.iconUrl ? (
              <img src={rec.manager.iconUrl} alt={rec.manager.name} className="w-full h-full object-cover" />
            ) : (
              <FontAwesomeIcon icon={faUserTie as IconProp} className="size-12 text-white/20" />
            )}
          </div>
        </div>

        {/* ── "It's a Match!" Hero Text — scales up ── */}
        <div
          className={`
            text-center flex flex-col items-center gap-2
            transition-all ease-out
            ${revealed ? 'opacity-100 scale-100 duration-600' : 'opacity-0 scale-50 duration-0'}
          `}
          style={{ transitionDelay: revealed ? '500ms' : '0ms' }}
        >
          <h2
            className="text-[2.5rem] md:text-[3.25rem] leading-none font-black text-white drop-shadow-[0_2px_20px_rgba(16,185,129,0.35)]"
            style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic' }}
          >
            It&apos;s a Match!
          </h2>
          <p className="text-white/50 text-base md:text-lg font-medium mt-1">
            You matched with{' '}
            <span className="text-emerald-300 font-bold">{rec.manager.name}</span>
          </p>
        </div>

        {/* ── Tags — fade in ── */}
        <div
          className={`
            flex flex-wrap items-center justify-center gap-2
            transition-all ease-out
            ${revealed ? 'opacity-100 translate-y-0 duration-500' : 'opacity-0 translate-y-4 duration-0'}
          `}
          style={{ transitionDelay: revealed ? '750ms' : '0ms' }}
        >
          {rec.manager.fundName && (
            <span className="px-4 py-1.5 bg-white/10 backdrop-blur-sm text-white/70 rounded-full text-xs font-bold uppercase tracking-wider border border-white/10">
              {rec.isDirectMatch ? 'Manages' : ''} {rec.manager.fundName}
            </span>
          )}
          {rec.manager.investmentStyle && (
            <span className="px-4 py-1.5 bg-emerald-400/15 text-emerald-300 rounded-full text-xs font-bold uppercase tracking-wider border border-emerald-400/20">
              {rec.manager.investmentStyle}
            </span>
          )}
          {rec.manager.riskTolerance && (
            <span className="px-4 py-1.5 bg-white/5 text-white/50 rounded-full text-xs font-bold uppercase tracking-wider border border-white/5">
              {rec.manager.riskTolerance} Risk
            </span>
          )}
        </div>

        {/* ── Direct match: manager profile blurb ── */}
        {rec.isDirectMatch && rec.manager.profile && (
          <p
            className={`
              text-white/40 text-sm leading-relaxed text-center max-w-md line-clamp-2 font-medium
              transition-all ease-out
              ${revealed ? 'opacity-100 translate-y-0 duration-500' : 'opacity-0 translate-y-4 duration-0'}
            `}
            style={{ transitionDelay: revealed ? '850ms' : '0ms' }}
          >
            {rec.manager.profile}
          </p>
        )}

        {/* ── Match Reason — slides up ── */}
        <div
          className={`
            bg-white/[0.07] backdrop-blur-sm rounded-2xl p-5 md:p-7 max-w-lg w-full text-center
            border border-white/10 group-hover:bg-white/[0.1]
            transition-all ease-out
            ${revealed ? 'opacity-100 translate-y-0 duration-600' : 'opacity-0 translate-y-6 duration-0'}
          `}
          style={{ transitionDelay: revealed ? '900ms' : '0ms' }}
        >
          <Text size="xs" className="text-emerald-300/60 uppercase tracking-[0.2em] font-black mb-3 block">
            Why You&apos;re a Match
          </Text>
          <Text size="base" className="text-white/80 leading-relaxed font-medium">
            {rec.matchReason}
          </Text>
        </div>

        {/* ── CTA Button — fades in last ── */}
        <Button
          className={`
            w-full max-w-sm text-sm h-14 bg-emerald-400 hover:bg-emerald-300 text-black
            border-0 rounded-2xl font-black uppercase tracking-widest gap-2
            shadow-xl shadow-emerald-500/20 hover:shadow-emerald-400/30
            hover:scale-[1.02] active:scale-[0.98]
            transition-all ease-out
            ${revealed ? 'opacity-100 translate-y-0 duration-500' : 'opacity-0 translate-y-6 duration-0'}
          `}
          style={{ transitionDelay: revealed ? '1050ms' : '0ms' }}
          onClick={() => {
            if (rec.isDirectMatch && activeHoldings?.holdings?.length > 0) {
              const fundStocks = activeHoldings.holdings.map((h: any) => ({
                ticker: h.stock?.ticker || '',
                percentage: h.portfolio_weight || 0,
              })).filter((s: any) => s.ticker);
              openPortfolioInvestModal(
                fundStocks,
                rec.manager.fundName || rec.manager.name,
                rec.matchReason || `Managed by ${rec.manager.name}`,
                undefined,
                'mindset_match_follow_strategy'
              );
            } else {
              if (blueprintAllStocks.length > 0) {
                openPortfolioInvestModal(
                  blueprintAllStocks.map((s: any) => ({
                    ticker: s.ticker,
                    percentage: s.percentage,
                  })),
                  `${rec.manager.name} Strategy`,
                  rec.matchReason || `Strategy inspired by ${rec.manager.name}`,
                  BLOOMIE_IMAGES[profile.id],
                  'mindset_match_follow_strategy'
                );
              }
            }
          }}
        >
          <FontAwesomeIcon icon={faMoneyBillTransfer as IconProp} className="size-5" />
          Follow Their Strategy
        </Button>
      </div>
    </div>
  );
};

const YourTypeResults: React.FC<YourTypeResultsProps> = ({ profile, answers, prefetchData, onRestart, onReplayWrapped }) => {
  const [hoveredAxis, setHoveredAxis] = useState<string | null>(null);
  const [expandedAllocation, setExpandedAllocation] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('header');
  const [selectedPortfolio, setSelectedPortfolio] = useState<any | null>(null);
  const [selectedStock, setSelectedStock] = useState<string | null>(null);
  const [getAppModalOpen, setGetAppModalOpen] = useState(false);
  const [getAppModalSource, setGetAppModalSource] = useState<string>('sticky_start_investing');
  const prevGetAppModalOpen = useRef(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [portfolioInvestModal, setPortfolioInvestModal] = useState<{
    open: boolean;
    stocks: PortfolioStock[];
    name: string;
    description: string;
    image?: any;
  }>({ open: false, stocks: [], name: '', description: '' });
  const { data: user } = useCurrentUser();

  const baseEventProps = {
    personalityType: profile?.id,
    riskTolerance: profile?.riskTolerance,
    timeHorizon: profile?.timeHorizon,
  };

  const requireTradingOrShowGetAppModal = (source: string) => {
    const canShowTradingFeatures = !!user?.canShowTradingFeatures;
    Analytics.track(YourTypeEvents.CTA_CLICK, {
      source,
      canShowTradingFeatures,
      ...baseEventProps,
    });

    if (canShowTradingFeatures) return true;

    Analytics.track(YourTypeEvents.TRADING_GATE_BLOCKED, {
      source,
      ...baseEventProps,
    });
    setGetAppModalSource(source);
    setGetAppModalOpen(true);
    return false;
  };

  const closeGetAppModal = () => {
    setGetAppModalOpen(false);
  };

  const openPortfolioInvestModal = (
    stocks: PortfolioStock[],
    name: string,
    description: string,
    image?: any,
    source = 'portfolio_invest'
  ) => {
    if (!requireTradingOrShowGetAppModal(source)) return;
    setPortfolioInvestModal({ open: true, stocks, name, description, image });
  };

  useEffect(() => {
    if (!prevGetAppModalOpen.current && getAppModalOpen) {
      Analytics.track(YourTypeEvents.GET_APP_MODAL_OPEN, {
        source: getAppModalSource,
        ...baseEventProps,
      });
    }
    if (prevGetAppModalOpen.current && !getAppModalOpen) {
      Analytics.track(YourTypeEvents.GET_APP_MODAL_CLOSE, {
        source: getAppModalSource,
        ...baseEventProps,
      });
    }
    prevGetAppModalOpen.current = getAppModalOpen;
  }, [getAppModalOpen, getAppModalSource]);

  const closePortfolioInvestModal = () => {
    setPortfolioInvestModal((prev) => ({ ...prev, open: false }));
  };

  const { data: recommendations, isLoading: isRecsLoading } = useQuery({
    queryKey: ['personalityRecommendations', profile.id, answers],
    queryFn: async () => {
      // If we already have prefetched recommendations, use them
      if (prefetchData?.recommendations) {
        return prefetchData.recommendations;
      }
      const data = await fetchPersonalityRecommendations({
        answers,
        personalityType: profile.id,
        riskTolerance: profile.riskTolerance,
        timeHorizon: profile.timeHorizon
      });
      return data;
    },
    enabled: !!answers && !!profile.id,
    staleTime: Infinity,
    initialData: prefetchData?.recommendations,
  });

  const aiPortfolio = prefetchData?.aiPortfolio;

  // Debug logging
  React.useEffect(() => {
    console.log('[YourTypeResults] prefetchData:', prefetchData);
    console.log('[YourTypeResults] aiPortfolio:', aiPortfolio);
    console.log('[YourTypeResults] aiPortfolio.indexes:', aiPortfolio?.indexes);
    console.log('[YourTypeResults] aiPortfolio.info:', aiPortfolio?.info);
  }, [prefetchData, aiPortfolio]);

  const primaryPortfolio = recommendations?.portfolios?.[0];
  const otherPortfolios = recommendations?.portfolios?.slice(1) || [];

  // Use primary portfolio as default selected
  useEffect(() => {
    if (primaryPortfolio && !selectedPortfolio) {
      setSelectedPortfolio(primaryPortfolio);
    }
  }, [primaryPortfolio]);

  // Fetch comprehensive fund details from Wall St Rank API via edge function
  const { data: fundDetails, isFetching: isFundDetailsFetching } = useQuery({
    queryKey: ['fundDetails', selectedPortfolio?.portfolio?.fundId],
    queryFn: async () => {
      if (!selectedPortfolio?.portfolio?.fundId) return null;
      // Use prefetched data if available for the primary fund
      if (
        prefetchData?.fundDetails &&
        selectedPortfolio.portfolio.fundId === primaryPortfolio?.portfolio?.fundId
      ) {
        return prefetchData.fundDetails;
      }
      return fetchFundDetails(selectedPortfolio.portfolio.fundId);
    },
    enabled: !!selectedPortfolio?.portfolio?.fundId,
    staleTime: 5 * 60 * 1000,
    initialData: (
      prefetchData?.fundDetails &&
      selectedPortfolio?.portfolio?.fundId === primaryPortfolio?.portfolio?.fundId
    ) ? prefetchData.fundDetails : undefined,
  });

  // Derive active data from fundDetails
  const activeHoldings = fundDetails?.holdings;
  const activeActivity = fundDetails?.topBuysSells;
  const fundStats = fundDetails?.details; // Full portfolio stats (AUM, returns, positions, etc.)
  const isSelectedFetching = isFundDetailsFetching;

  const pieData = useMemo(() => {
    if (!activeHoldings?.holdings) return [];

    // Based on API: h.stock.ticker and h.portfolio_weight
    const topHoldings = activeHoldings.holdings.slice(0, 8).map((h: any) => ({
      name: h.stock?.ticker || 'Unknown',
      value: h.portfolio_weight || 0
    }));

    const topWeight = topHoldings.reduce((acc: number, curr: any) => acc + curr.value, 0);
    if (topWeight < 95 && topWeight > 0) {
      topHoldings.push({
        name: 'Others',
        value: Math.max(0, 100 - topWeight)
      });
    }

    return topHoldings.filter((h: any) => h.value > 0);
  }, [activeHoldings]);

  // Detect whether AI returned percentages as decimals (0.08 = 8%) or whole numbers (8 = 8%).
  // Sample/fallback data uses decimals; live AI responses typically use whole numbers.
  const portfolioMultiplier = useMemo(() => {
    const tickers = aiPortfolio?.indexes?.[0]?.tickers;
    if (!tickers?.length) return 100;
    const sum = tickers.reduce((acc: number, t: any) => acc + (t.percentage || 0), 0);
    // If the raw sum is already close to 100, percentages are whole numbers — no multiplication needed.
    return sum > 1.5 ? 1 : 100;
  }, [aiPortfolio]);

  const blueprintPieData = useMemo(() => {
    if (!aiPortfolio?.indexes?.[0]?.tickers) return [];
    return aiPortfolio.indexes[0].tickers.slice(0, 8).map((t: any) => ({
      name: t.ticker,
      value: (t.percentage || 0) * portfolioMultiplier
    }));
  }, [aiPortfolio, portfolioMultiplier]);

  const blueprintAllStocks = useMemo(() => {
    if (!aiPortfolio?.indexes?.[0]?.tickers) return [];
    return aiPortfolio.indexes[0].tickers.map((t: any) => ({
      ticker: t.ticker,
      percentage: (t.percentage || 0) * portfolioMultiplier,
      text: t.text || ''
    }));
  }, [aiPortfolio, portfolioMultiplier]);

  const navItems = [
    { id: 'diagnosis', icon: faBrain, label: 'Diagnosis' },
    { id: 'portfolio', icon: faWallet, label: 'Portfolio' },
    { id: 'funds', icon: faUserTie, label: 'Managers' },
    { id: 'blueprint', icon: faChartPie, label: 'Blueprint' },
  ];

  useEffect(() => {
    const observers = new Map();
    const options = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5],
    };

    const callback = (entries: IntersectionObserverEntry[]) => {
      const intersecting = entries.filter(e => e.isIntersecting);
      if (intersecting.length === 0) return;

      intersecting.sort((a, b) => {
        const aRect = a.target.getBoundingClientRect();
        const bRect = b.target.getBoundingClientRect();
        return aRect.top - bRect.top;
      });

      const topmost = intersecting[0];
      setActiveSection(topmost.target.id);
    };

    const observer = new IntersectionObserver(callback, options);
    navItems.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const allTickers = useMemo(() => {
    const allocationTickers = profile.allocation.flatMap(a => a.examples);
    const holdingTickers = activeHoldings?.holdings?.map((h: any) => h.stock?.ticker) || [];
    const buyTickers = activeActivity?.buys?.map((b: any) => b.stock?.ticker) || [];
    const sellTickers = activeActivity?.sells?.map((s: any) => s.stock?.ticker) || [];
    return Array.from(new Set([...profile.sampleHoldings, ...allocationTickers, ...holdingTickers, ...buyTickers, ...sellTickers]))
      .filter(t => !!t && typeof t === 'string' && t !== 'undefined');
  }, [profile, activeHoldings, activeActivity]);

  const { data: quotes } = useQuery({
    queryKey: ['batchQuotes', allTickers],
    queryFn: () => AssetService.getBatchQuote(allTickers),
    enabled: allTickers.length > 0,
    refetchInterval: 30000,
  });


  return (
    <div className="flex flex-col gap-16 py-12 -mx-5 md:mx-auto md:px-6 max-w-2xl animate-in fade-in duration-1000 relative">
      {/* Mini Sidebar Navigation */}
      <div className="hidden lg:flex fixed right-8 top-1/2 -translate-y-1/2 flex-col gap-4 z-50">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => scrollTo(item.id)}
            className="group relative flex items-center justify-end"
          >
            <div className={`
              absolute right-10 px-3 py-1.5 rounded-xl bg-black/80 backdrop-blur-md border border-white/10 
              transition-all duration-300 transform origin-right
              ${activeSection === item.id ? 'opacity-100 scale-100' : 'opacity-0 scale-90 translate-x-4 pointer-events-none group-hover:opacity-100 group-hover:scale-100 group-hover:translate-x-0'}
            `}>
              <Text size="xs" className="text-white font-bold whitespace-nowrap uppercase tracking-widest">{item.label}</Text>
            </div>
            <div className={`
              size-10 rounded-full flex items-center justify-center border transition-all duration-300
              ${activeSection === item.id ? 'bg-teal-200 border-teal-200 text-black shadow-[0_0_20px_rgba(153,246,228,0.3)]' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10 hover:border-white/20 hover:text-white/60'}
            `}>
              <FontAwesomeIcon icon={item.icon as IconProp} className="size-4" />
            </div>
          </button>
        ))}
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* 01 — YOUR DIAGNOSIS                                    */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div id="diagnosis" className="scroll-mt-24 mx-4 md:mx-0 flex flex-col gap-0">
        {/* Section Label */}
        <div className="flex items-center gap-4 mb-6">
          <Text size="xs" className="text-teal-200/40 font-black tracking-[0.3em] uppercase flex-none">01</Text>
          <div className="h-px flex-1 bg-gradient-to-r from-teal-200/20 to-transparent" />
          <Text size="xs" className="text-white/20 font-bold uppercase tracking-[0.2em] flex-none">Your Diagnosis</Text>
        </div>

        {/* Cohesive Diagnosis Container */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-[2.5rem] overflow-hidden">
          {/* Hero — Name + Traits */}
          <div className="text-center flex flex-col gap-4 relative px-6 md:px-10 pt-10 pb-8">
            <Text size="sm" className="text-teal-200 uppercase tracking-[0.2em] font-bold">
              Your Investment Personality
            </Text>
            {/* Bloomie Character Image */}
            {BLOOMIE_IMAGES[profile.id] && (
              <div className="flex justify-center my-2">
                <div className="relative rounded-[1.75rem] bg-gradient-to-br from-teal-200/10 via-white/5 to-transparent border border-white/[0.08] shadow-[0_0_40px_rgba(45,212,191,0.08)] flex items-center justify-center overflow-hidden">
                  {/* Inner glow ring */}
                  <div className="absolute inset-[1px] rounded-[1.6rem] bg-gradient-to-b from-white/[0.04] to-transparent pointer-events-none" />
                  {/* Subtle radial highlight */}
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(45,212,191,0.06),transparent_70%)] pointer-events-none" />
                  <Image
                    src={BLOOMIE_IMAGES[profile.id]}
                    alt={profile.name}
                    width={160}
                    height={160}
                    className="size-28 md:size-36 object-contain drop-shadow-2xl relative z-10"
                    priority
                  />
                </div>
              </div>
            )}
            <h1 className="text-3xl md:text-5xl font-bold leading-tight uppercase bg-clip-text text-transparent bg-gradient-to-r from-white to-white/60">
              {profile.name}
            </h1>
            <div className="flex flex-wrap justify-center gap-2 mt-2">
              {profile.traits.map((trait) => (
                <span key={trait} className="px-4 py-1 bg-teal-200 text-black rounded-full text-xs uppercase font-bold tracking-wider">
                  {trait}
                </span>
              ))}
            </div>

            {/* Profile description — flows directly under traits */}
            <div className="mt-4 max-w-lg mx-auto">
              <span
                className="text-base text-white/50 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: profile.description }}
              />
            </div>

            {/* Quick Stats — integrated into the hero */}
            <div className="flex justify-center gap-6 md:gap-10 mt-4">
              <div className="flex flex-col items-center gap-0.5">
                <Text className="text-[10px] md:text-xs text-white/25 uppercase tracking-wider font-bold">Style</Text>
                <Text className="text-sm md:text-base font-bold text-white/80">{profile.investmentStyle}</Text>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="flex flex-col items-center gap-0.5">
                <Text className="text-[10px] md:text-xs text-white/25 uppercase tracking-wider font-bold">Risk</Text>
                <Text className="text-sm md:text-base font-bold text-white/80">{profile.riskTolerance}</Text>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="flex flex-col items-center gap-0.5">
                <Text className="text-[10px] md:text-xs text-white/25 uppercase tracking-wider font-bold">Horizon</Text>
                <Text className="text-sm md:text-base font-bold text-white/80">{profile.timeHorizon}</Text>
              </div>
            </div>
          </div>

          {/* Thin divider */}
          <div className="mx-8 md:mx-12 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />

          {/* Deep Analysis Triptych */}
          <div className="px-2 md:px-4 py-6 md:py-8">
            <DeepAnalysisSection analysis={profile.detailedAnalysis} />
          </div>

          {/* Thin divider */}
          <div className="mx-8 md:mx-12 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />

          {/* Radar Chart — seamless within container */}
          <div className="px-4 md:px-6 py-6 md:py-8">
            <div className="h-[280px] md:h-[400px] w-full relative group flex flex-col">
              <div className="flex justify-between items-center mb-2 md:mb-4 px-2 md:px-0 flex-shrink-0">
                <Text size="sm" className="text-teal-200 uppercase tracking-widest font-bold">Performance DNA</Text>
                <div className="flex items-center gap-2 text-white/20 group-hover:text-white/40 transition-colors">
                  <FontAwesomeIcon icon={faInfoCircle as IconProp} className="size-4" />
                  <Text size="sm" className="uppercase tracking-widest hidden md:block">Hover to explore</Text>
                </div>
              </div>

              <div className="flex-1 min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart
                    cx="50%"
                    cy="50%"
                    outerRadius="80%"
                    data={profile.radarData}
                    margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                  >
                    <PolarGrid stroke="#ffffff10" />
                    <PolarAngleAxis
                      dataKey="subject"
                      tick={({ x, y, payload, ...props }: any) => (
                        <g
                          onMouseEnter={() => setHoveredAxis(payload.value)}
                          onMouseLeave={() => setHoveredAxis(null)}
                          className="cursor-help"
                        >
                          <text
                            x={x}
                            y={y}
                            textAnchor={props.textAnchor}
                            fill={hoveredAxis === payload.value ? "#99f6e4" : "#ffffff60"}
                            className={`transition-all duration-300 text-[9px] md:text-[13px] uppercase tracking-wider ${hoveredAxis === payload.value ? "font-bold" : "font-medium"}`}
                          >
                            {payload.value}
                          </text>
                        </g>
                      )}
                    />
                    <Radar
                      name="Score"
                      dataKey="A"
                      stroke="#99f6e4"
                      fill="#99f6e4"
                      fillOpacity={0.7}
                      className="hover:fill-opacity-90 transition-all duration-500 cursor-crosshair"
                    />
                    <Tooltip content={<CustomRadarTooltip />} cursor={false} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-full px-6 pointer-events-none">
                <div className={`
                  bg-teal-200 text-black px-4 py-2 md:px-6 md:py-3 rounded-2xl shadow-2xl transition-all duration-500 transform
                  ${hoveredAxis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                `}>
                  <Text size="sm" className="font-bold flex flex-col gap-0.5 text-center leading-tight">
                    <span className="uppercase tracking-wider opacity-60 text-xs">{hoveredAxis}</span>
                    <span className="text-sm md:text-base">{hoveredAxis && AXIS_EXPLANATIONS[hoveredAxis]}</span>
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* 02 — PORTFOLIO MATCH                                   */}
      {/* ═══════════════════════════════════════════════════════ */}

      {/* Portfolio Allocation */}
      <div id="portfolio" className="flex flex-col gap-6 scroll-mt-24 px-4 md:px-0">
        <div className="flex items-center gap-4">
          <Text size="xs" className="text-teal-200/40 font-black tracking-[0.3em] uppercase flex-none">02</Text>
          <div className="h-px flex-1 bg-gradient-to-r from-teal-200/20 to-transparent" />
          <Text size="xs" className="text-white/20 font-bold uppercase tracking-[0.2em] flex-none">Institutional Match</Text>
        </div>
        <div className="flex flex-col gap-4">

          {isRecsLoading ? (
            <div className="bg-white/5 border border-white/10 rounded-[2.5rem] p-12 flex flex-col items-center gap-4 animate-pulse">
              <FontAwesomeIcon icon={faRotateLeft as IconProp} className="size-8 text-teal-200 animate-spin" />
              <Text size="base" className="text-white/40 font-bold uppercase tracking-widest">Analyzing 100 Portfolios...</Text>
            </div>
          ) : selectedPortfolio ? (
            <div className="flex flex-col gap-6">
              {/* Portfolio Selection List */}
              <div className="flex flex-col gap-3">
                <Text size="sm" className="text-white/40 uppercase tracking-widest font-bold ml-2">Top Matches For You</Text>
                {(() => {
                  const portfolios = recommendations?.portfolios || [];
                  const scores = portfolios.map((r: any) => r.matchScore);
                  const minS = Math.min(...scores);
                  const maxS = Math.max(...scores);
                  return (
                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide [&::-webkit-scrollbar]:hidden -mx-4 px-4 md:mx-0 md:px-0">
                      {portfolios.map((rec: any, idx: number) => (
                        <button
                          key={rec.portfolio.fundId}
                          onClick={() => setSelectedPortfolio(rec)}
                          className={`
                            flex-none flex items-center gap-3 p-3 pr-4 rounded-2xl border transition-all duration-300
                            ${selectedPortfolio.portfolio.fundId === rec.portfolio.fundId
                              ? 'bg-teal-200 border-teal-200 text-black shadow-[0_0_20px_rgba(153,246,228,0.2)]'
                              : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}
                          `}
                        >
                          <div className={`size-10 rounded-xl flex items-center justify-center overflow-hidden p-1.5 ${selectedPortfolio.portfolio.fundId === rec.portfolio.fundId ? 'bg-black/10' : 'bg-black/40'}`}>
                            {rec.portfolio.iconUrl ? (
                              <img src={rec.portfolio.iconUrl} alt="" className="w-full h-full object-contain" />
                            ) : (
                              <FontAwesomeIcon icon={faWallet as IconProp} className="size-5 opacity-40" />
                            )}
                          </div>
                          <div className="flex flex-col items-start gap-0.5">
                            <Text size="sm" className="font-bold uppercase tracking-tight leading-none truncate max-w-[120px]">
                              {rec.portfolio.fundName}
                            </Text>
                          </div>
                          <MatchGauge score={rec.matchScore} minScore={minS} maxScore={maxS} rank={idx} total={portfolios.length} size="sm" inverted={selectedPortfolio.portfolio.fundId === rec.portfolio.fundId} />
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Selected Portfolio Detail Card */}
              <div className="bg-white/5 border border-white/10 rounded-[2.5rem] overflow-hidden group hover:border-teal-200/30 transition-all duration-500 relative">
                {isSelectedFetching && (
                  <div className="absolute inset-0 bg-black/40 backdrop-blur-sm z-10 flex items-center justify-center">
                    <FontAwesomeIcon icon={faRotateLeft as IconProp} className="size-8 text-teal-200 animate-spin" />
                  </div>
                )}
                <div className="px-4 md:px-6 py-6 md:py-8 flex flex-col gap-6 md:gap-8">
                  {/* Header */}
                  <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:gap-6">
                    <div className="size-16 rounded-2xl bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden p-2 flex-none">
                      {selectedPortfolio.portfolio.iconUrl ? (
                        <img src={selectedPortfolio.portfolio.iconUrl} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <FontAwesomeIcon icon={faWallet as IconProp} className="size-8 text-white/20" />
                      )}
                    </div>
                    <div className="flex flex-col gap-1 flex-1">
                      <Text size="lg" className="font-bold text-white uppercase tracking-tight leading-tight">{selectedPortfolio.portfolio.fundName}</Text>
                    </div>
                    {(() => {
                      const portfolios = recommendations?.portfolios || [];
                      const scores = portfolios.map((r: any) => r.matchScore);
                      const minS = Math.min(...scores);
                      const maxS = Math.max(...scores);
                      const selectedRank = portfolios.findIndex((r: any) => r.portfolio.fundId === selectedPortfolio.portfolio.fundId);
                      return <MatchGauge score={selectedPortfolio.matchScore} minScore={minS} maxScore={maxS} rank={Math.max(0, selectedRank)} total={portfolios.length} size="md" />;
                    })()}
                  </div>

                  <div className="bg-teal-200/5 border border-teal-200/20 p-6 rounded-[2.5rem] flex flex-col gap-3 group-hover:bg-teal-200/10 transition-all duration-500">
                    <Text size="sm" className="text-teal-200 uppercase tracking-widest font-black flex items-center gap-2">
                      <FontAwesomeIcon icon={faBrain as IconProp} className="size-3 opacity-60" />
                      Strategic Synergy
                    </Text>
                    <Text size="base" className="text-white/80 leading-relaxed font-medium">
                      {selectedPortfolio.personalizedExplanation}
                    </Text>
                  </div>

                  {/* Key Metrics Grid */}
                  <div className="grid grid-cols-3 gap-2 md:gap-4">
                    {/* AUM */}
                    <div className="bg-white/5 p-3 md:p-5 rounded-2xl border border-white/5 flex flex-col gap-1 md:gap-2 hover:bg-white/10 transition-colors">
                      <Text className="text-[10px] md:text-sm text-secondary uppercase tracking-wider font-bold">AUM</Text>
                      <Text className="text-sm md:text-lg font-bold leading-tight break-words">
                        {formatLargeNumber(fundStats?.aum || selectedPortfolio.portfolio.aum)}
                      </Text>
                      {fundStats?.aum_change_percent != null && (
                        <Text className={`text-[10px] font-bold ${fundStats.aum_change_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {fundStats.aum_change_percent >= 0 ? '+' : ''}{fundStats.aum_change_percent.toFixed(1)}% QoQ
                        </Text>
                      )}
                    </div>
                    {/* 1Y Return */}
                    <div className="bg-white/5 p-3 md:p-5 rounded-2xl border border-white/5 flex flex-col gap-1 md:gap-2 hover:bg-white/10 transition-colors">
                      <Text className="text-[10px] md:text-sm text-secondary uppercase tracking-wider font-bold">1Y Return</Text>
                      <Text className={`text-sm md:text-lg font-bold leading-tight break-words ${(fundStats?.post_disclosure_return_years_1 ?? selectedPortfolio.portfolio.returnYears1) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(fundStats?.post_disclosure_return_years_1 ?? selectedPortfolio.portfolio.returnYears1) >= 0 ? '+' : ''}
                        {(fundStats?.post_disclosure_return_years_1 ?? selectedPortfolio.portfolio.returnYears1)?.toFixed(1)}%
                      </Text>
                    </div>
                    {/* 5Y Return */}
                    <div className="bg-white/5 p-3 md:p-5 rounded-2xl border border-white/5 flex flex-col gap-1 md:gap-2 hover:bg-white/10 transition-colors">
                      <Text className="text-[10px] md:text-sm text-secondary uppercase tracking-wider font-bold">5Y Return</Text>
                      <Text className={`text-sm md:text-lg font-bold leading-tight break-words ${(fundStats?.post_disclosure_return_years_5) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {fundStats?.post_disclosure_return_years_5 != null
                          ? `${fundStats.post_disclosure_return_years_5 >= 0 ? '+' : ''}${fundStats.post_disclosure_return_years_5.toFixed(1)}%`
                          : '—'}
                      </Text>
                    </div>
                  </div>

                  {/* Extended Metrics Row */}
                  {fundStats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
                      <div className="bg-white/5 p-3 rounded-2xl border border-white/5 flex flex-col gap-1 hover:bg-white/10 transition-colors">
                        <Text className="text-[10px] text-secondary uppercase tracking-wider font-bold">Capital Flow</Text>
                        <Text className="text-sm font-bold">{formatLargeNumber(fundStats.capital_flow)}</Text>
                        {fundStats.capital_flow_as_percent_of_aum != null && (
                          <Text className="text-[10px] text-white/30 font-bold">{fundStats.capital_flow_as_percent_of_aum.toFixed(2)}% of AUM</Text>
                        )}
                      </div>
                      <div className="bg-white/5 p-3 rounded-2xl border border-white/5 flex flex-col gap-1 hover:bg-white/10 transition-colors">
                        <Text className="text-[10px] text-secondary uppercase tracking-wider font-bold">Turnover</Text>
                        <Text className="text-sm font-bold">{fundStats.turnover_percent?.toFixed(1) ?? '—'}%</Text>
                      </div>
                      <div className="bg-white/5 p-3 rounded-2xl border border-white/5 flex flex-col gap-1 hover:bg-white/10 transition-colors">
                        <Text className="text-[10px] text-secondary uppercase tracking-wider font-bold">Holdings</Text>
                        <Text className="text-sm font-bold">{fundStats.holdings?.toLocaleString() ?? '—'}</Text>
                        {fundStats.holdings_change != null && fundStats.holdings_change !== 0 && (
                          <Text className={`text-[10px] font-bold ${fundStats.holdings_change > 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {fundStats.holdings_change > 0 ? '+' : ''}{fundStats.holdings_change}
                          </Text>
                        )}
                      </div>
                      <div className="bg-white/5 p-3 rounded-2xl border border-white/5 flex flex-col gap-1 hover:bg-white/10 transition-colors">
                        <Text className="text-[10px] text-secondary uppercase tracking-wider font-bold">ETF %</Text>
                        <Text className="text-sm font-bold">{fundStats.etfs_percent?.toFixed(1) ?? '—'}%</Text>
                      </div>
                    </div>
                  )}

                  {activeHoldings && pieData.length > 0 && (
                    <div className="flex flex-col gap-4 pt-4 border-t border-white/5">
                      <Text size="sm" className="text-white/40 uppercase tracking-widest font-bold">Portfolio Distribution</Text>
                      <div className="bg-black/20 rounded-3xl p-6 flex flex-col md:flex-row items-center gap-6">
                        <div className="w-full md:w-1/2 h-[180px] relative">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={45}
                                outerRadius={65}
                                paddingAngle={5}
                                dataKey="value"
                                animationBegin={0}
                                animationDuration={1000}
                              >
                                {pieData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} stroke="none" />
                                ))}
                              </Pie>
                              <Tooltip content={<CustomPieTooltip />} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <Text size="xs" className="text-white/20 uppercase font-black tracking-tighter text-xs">Assets</Text>
                            <Text size="xl" className="font-bold text-teal-200 leading-none">
                              {pieData.length - (pieData.find(d => d.name === 'Others') ? 1 : 0)}
                            </Text>
                          </div>
                        </div>
                        <div className="w-full md:w-1/2 grid grid-cols-2 gap-x-4 gap-y-3">
                          {pieData.map((entry, index) => (
                            <LegendItem
                              key={entry.name}
                              symbol={entry.name}
                              weight={entry.value}
                              color={PIE_COLORS[index % PIE_COLORS.length]}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Performance Chart */}
                  {(fundDetails?.returnBars1Y || fundDetails?.returnBars5Y) && (
                    <FundPerformanceChart
                      returnBars1Y={fundDetails.returnBars1Y}
                      returnBars5Y={fundDetails.returnBars5Y}
                    />
                  )}

                  {/* Trade Activity — Live from Wall St Rank */}
                  {(activeActivity?.buys?.length > 0 || activeActivity?.sells?.length > 0) && (
                    <div className="flex flex-col gap-2 md:gap-4 pt-4 border-t border-white/5">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 md:gap-3">
                        <div className="flex items-center gap-3">
                          <FontAwesomeIcon icon={faMoneyBillTransfer as IconProp} className="size-4 text-teal-200/60" />
                          <Text size="sm" className="text-white/40 uppercase tracking-widest font-bold">Top Buys & Sells</Text>
                        </div>
                        <div className="flex gap-4">
                          <div className="flex items-center gap-2">
                            <div className="size-1.5 rounded-full bg-green-500" />
                            <Text size="sm" className="text-white/40 font-bold uppercase tracking-wider text-sm">Buys</Text>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="size-1.5 rounded-full bg-red-500" />
                            <Text size="sm" className="text-white/40 font-bold uppercase tracking-wider text-sm">Sells</Text>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 md:gap-4">
                        <div className="flex flex-col gap-1 md:gap-2">
                          {(activeActivity?.buys || []).slice(0, 3).map((buy: any, idx: number) => (
                            <button
                              key={idx}
                              onClick={() => buy.stock?.ticker && setSelectedStock(buy.stock.ticker)}
                              className="flex items-center justify-between p-2 md:p-3 bg-green-500/5 border border-green-500/10 rounded-xl hover:bg-green-500/10 transition-all group/buy"
                            >
                              <div className="flex items-center gap-2 md:gap-3 overflow-hidden">
                                <Text size="sm" className="font-black text-green-400 flex-none text-xs md:text-sm">{buy.stock?.ticker || '???'}</Text>
                                <Text size="sm" className="text-white/60 truncate text-xs md:text-sm">{buy.stock?.name}</Text>
                              </div>
                              <div className="flex items-center gap-2 flex-none">
                                {buy.shares_change_percent != null && (
                                  <Text size="sm" className="text-green-400/60 font-bold text-xs">
                                    +{buy.shares_change_percent.toFixed(1)}%
                                  </Text>
                                )}
                                <FontAwesomeIcon icon={faArrowTrendUp as IconProp} className="size-3 text-green-500/40 group-hover/buy:text-green-400 transition-colors" />
                              </div>
                            </button>
                          ))}
                          {(!activeActivity?.buys || activeActivity.buys.length === 0) && (
                            <div className="p-2 md:p-3 bg-white/5 border border-white/5 rounded-xl opacity-40">
                              <Text size="sm" className="text-xs md:text-sm">No major buys this quarter</Text>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 md:gap-2">
                          {(activeActivity?.sells || []).slice(0, 3).map((sell: any, idx: number) => (
                            <button
                              key={idx}
                              onClick={() => sell.stock?.ticker && setSelectedStock(sell.stock.ticker)}
                              className="flex items-center justify-between p-2 md:p-3 bg-red-500/5 border border-red-500/10 rounded-xl hover:bg-red-500/10 transition-all group/sell"
                            >
                              <div className="flex items-center gap-2 md:gap-3 overflow-hidden">
                                <Text size="sm" className="font-black text-red-400 flex-none text-xs md:text-sm">{sell.stock?.ticker || '???'}</Text>
                                <Text size="sm" className="text-white/60 truncate text-xs md:text-sm">{sell.stock?.name}</Text>
                              </div>
                              <div className="flex items-center gap-2 flex-none">
                                {sell.shares_change_percent != null && (
                                  <Text size="sm" className="text-red-400/60 font-bold text-xs">
                                    {sell.shares_change_percent.toFixed(1)}%
                                  </Text>
                                )}
                                <FontAwesomeIcon icon={faArrowTrendDown as IconProp} className="size-3 text-red-500/40 group-hover/sell:text-red-400 transition-colors" />
                              </div>
                            </button>
                          ))}
                          {(!activeActivity?.sells || activeActivity.sells.length === 0) && (
                            <div className="p-2 md:p-3 bg-white/5 border border-white/5 rounded-xl opacity-40">
                              <Text size="sm" className="text-xs md:text-sm">No major sells this quarter</Text>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Position Activity Summary */}
                  <PositionActivityCard details={fundStats} />

                  {/* Invest in Fund CTA */}
                  {activeHoldings?.holdings?.length > 0 && (
                    <div className="pt-6 border-t border-white/5">
                      <Button
                        type={ButtonType.Primary}
                        className="w-full h-14 rounded-2xl font-bold uppercase tracking-widest bg-teal-200 text-black hover:bg-teal-300 gap-3 shadow-lg shadow-teal-200/10"
                        onClick={() => {
                          const fundStocks = activeHoldings.holdings.map((h: any) => ({
                            ticker: h.stock?.ticker || '',
                            percentage: h.portfolio_weight || 0,
                          })).filter((s: any) => s.ticker);
                          openPortfolioInvestModal(
                            fundStocks,
                            selectedPortfolio.portfolio.fundName || 'Fund Portfolio',
                            selectedPortfolio.personalizedExplanation || `Institutional fund portfolio`,
                            undefined,
                            'institutional_follow_strategy'
                          );
                        }}
                      >
                        <FontAwesomeIcon icon={faMoneyBillTransfer as IconProp} className="size-5" />
                        Follow their strategy
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-teal-200/10 border-l-4 border-teal-200 p-6 rounded-r-[2rem] flex flex-col gap-3">
              <Text size="sm" className="text-teal-200 uppercase tracking-widest font-bold flex items-center gap-2">
                <FontAwesomeIcon icon={faInfoCircle as IconProp} className="size-3" />
                Why This Fits You
              </Text>
              <span
                className="text-base text-white/80 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: profile.portfolioAnalysis }}
              />
            </div>
          )}
        </div>

        {!primaryPortfolio && (
          <div className="flex flex-col gap-2">
            {profile.allocation.map((item) => (
              <div
                key={item.name}
                className={`
                flex flex-col group cursor-pointer px-4 -mx-4 rounded-3xl transition-all duration-300
                ${expandedAllocation === item.name ? 'bg-white/10 py-4 gap-2' : 'hover:bg-white/5 py-3 gap-1'}
              `}
                onClick={() => setExpandedAllocation(expandedAllocation === item.name ? null : item.name)}
              >
                <div className="flex justify-between items-center text-base">
                  <div className="flex items-center gap-3">
                    <Text className={`transition-colors ${expandedAllocation === item.name ? 'text-teal-200 font-bold' : 'text-white group-hover:text-teal-200'}`}>
                      {item.name}
                    </Text>
                    <FontAwesomeIcon
                      icon={faChevronDown as IconProp}
                      className={`size-3 transition-all duration-300 ${expandedAllocation === item.name
                        ? 'text-teal-200 rotate-180 opacity-100'
                        : 'text-white/40 group-hover:text-teal-200'
                        }`}
                    />
                  </div>
                  <Text className="text-teal-200 font-bold">{item.value}%</Text>
                </div>
                <div className="w-full bg-white/5 h-1 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${expandedAllocation === item.name ? 'bg-teal-300 shadow-[0_0_10px_rgba(153,246,228,0.5)]' : 'bg-teal-200 opacity-60'}`}
                    style={{ width: `${item.value}%` }}
                  />
                </div>

                <div className={`
                grid transition-all duration-300 ease-in-out
                ${expandedAllocation === item.name ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'}
              `}>
                  <div className="overflow-hidden flex flex-col gap-4">
                    <span
                      className="text-base text-white/70 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: item.explanation }}
                    />
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-white/10" />
                        <Text size="sm" className="text-teal-200 uppercase tracking-widest font-bold opacity-60">Key Examples</Text>
                        <div className="h-px flex-1 bg-white/10" />
                      </div>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {item.examples.map(ex => (
                          <HoldingItem
                            key={ex}
                            symbol={ex}
                            quote={quotes?.[ex]}
                            mini
                            onSelect={setSelectedStock}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* 03 — MINDSET MATCH — "It's a Match!" Tinder-style       */}
      {/* ═══════════════════════════════════════════════════════ */}

      <div id="funds" className="flex flex-col gap-6 scroll-mt-24 px-4 md:px-0">
        <div className="flex items-center gap-4">
          <Text size="xs" className="text-teal-200/40 font-black tracking-[0.3em] uppercase flex-none">03</Text>
          <div className="h-px flex-1 bg-gradient-to-r from-teal-200/20 to-transparent" />
          <Text size="xs" className="text-white/20 font-bold uppercase tracking-[0.2em] flex-none">Mindset Match</Text>
        </div>

        <div className="flex flex-col gap-6">
          {recommendations?.managers?.length > 0 ? (
            recommendations.managers.map((rec: any, idx: number) => (
              <ManagerMatchReveal
                key={idx}
                rec={rec}
                idx={idx}
                profile={profile}
                activeHoldings={activeHoldings}
                blueprintAllStocks={blueprintAllStocks}
                openPortfolioInvestModal={openPortfolioInvestModal}
                requireTradingOrShowGetAppModal={requireTradingOrShowGetAppModal}
              />
            ))
          ) : (
            profile.topFunds.map((fund) => (
              <div key={fund.name} className="group bg-white/5 hover:bg-white/10 border border-white/10 px-4 md:px-6 py-6 md:py-8 rounded-[2.5rem] transition-all duration-300 shadow-xl">
                <div className="flex justify-between items-start mb-4 gap-4">
                  <div className="flex flex-col gap-1">
                    <Text size="lg" className="font-bold text-white uppercase tracking-tight">{fund.name}</Text>
                    {fund.manager && <Text size="sm" className="text-secondary font-medium">Managed by {fund.manager}</Text>}
                  </div>
                  <span className="flex-none px-3 py-1 bg-teal-200 text-black rounded-full text-xs uppercase font-bold border border-teal-200/20 tracking-widest shadow-lg shadow-teal-200/20">
                    {fund.risk} Risk
                  </span>
                </div>

                <div className="flex flex-col gap-4 mb-6">
                  <div className="flex flex-col gap-1">
                    <Text size="sm" className="text-teal-200 uppercase tracking-widest font-bold">Investment Thesis</Text>
                    <span
                      className="text-base text-white/80 leading-relaxed font-medium"
                      dangerouslySetInnerHTML={{ __html: fund.thesis }}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Text size="sm" className="text-teal-200 uppercase tracking-widest font-bold">Why it matches you</Text>
                    <span
                      className="text-base text-white/80 leading-relaxed font-medium"
                      dangerouslySetInnerHTML={{ __html: fund.matchReason }}
                    />
                  </div>
                </div>

                <Button
                  className="w-full text-sm h-12 bg-white/5 hover:bg-teal-200 hover:text-black transition-all border border-white/5 hover:border-teal-200 rounded-2xl font-black uppercase tracking-widest gap-2"
                  onClick={() => {
                    if (blueprintAllStocks.length > 0) {
                      openPortfolioInvestModal(
                        blueprintAllStocks.map((s: any) => ({
                          ticker: s.ticker,
                          percentage: s.percentage,
                        })),
                        fund.name,
                        fund.thesis || `Fund strategy from ${fund.name}`,
                        BLOOMIE_IMAGES[profile.id],
                        'mindset_copy_fund'
                      );
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faMoneyBillTransfer as IconProp} className="size-4" />
                  Copy this fund
                </Button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* 04 — AI BLUEPRINT                                      */}
      {/* ═══════════════════════════════════════════════════════ */}

      <div id="blueprint" className="flex flex-col gap-6 scroll-mt-24 px-4 md:px-0">
        <div className="flex items-center gap-4">
          <Text size="xs" className="text-teal-200/40 font-black tracking-[0.3em] uppercase flex-none">04</Text>
          <div className="h-px flex-1 bg-gradient-to-r from-teal-200/20 to-transparent" />
          <Text size="xs" className="text-white/20 font-bold uppercase tracking-[0.2em] flex-none">AI Blueprint</Text>
        </div>

        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold uppercase">Bloomie Custom Portfolio</h3>
          <div className="px-3 py-1.5 bg-teal-200/10 border border-teal-200/20 rounded-xl flex items-center gap-2">
            <FontAwesomeIcon icon={faRocket as IconProp} className="size-3 text-teal-200" />
            <Text size="xs" className="text-teal-200 uppercase font-bold tracking-widest">Custom Engine</Text>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white/10 to-transparent border border-white/10 rounded-[2.5rem] px-4 md:px-6 py-6 md:py-8 flex flex-col gap-8 shadow-2xl">
          {blueprintPieData.length > 0 ? (
            <>
              {/* Overview Section */}
              <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
                <div className="w-48 md:w-2/5 aspect-square relative flex items-center justify-center bg-black/20 rounded-full border border-teal-200/5 shadow-inner">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={blueprintPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        stroke="none"
                      >
                        {blueprintPieData.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} className="hover:opacity-80 transition-opacity outline-none" />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute flex flex-col items-center">
                    <FontAwesomeIcon icon={faChartPie as IconProp} className="size-6 text-teal-200/40 mb-1" />
                    <Text className="text-xs font-black uppercase tracking-tighter text-white/20 text-center">Custom<br />Strategy</Text>
                  </div>
                </div>
                <div className="w-full md:w-3/5 flex flex-col gap-4 text-center md:text-left">
                  {aiPortfolio?.isSampleData && (
                    <Text size="sm" className="text-yellow-400 font-bold uppercase tracking-widest bg-yellow-400/10 px-3 py-1 rounded-full border border-yellow-400/20 inline-block w-fit mx-auto md:mx-0">
                      Sample Data (Quota Limit)
                    </Text>
                  )}
                  <Text size="lg" className="font-bold text-white uppercase tracking-tight">{aiPortfolio?.info?.name || "The Custom Index"}</Text>
                  <Text className="text-white/60 leading-relaxed text-base">
                    {aiPortfolio?.info?.description || "A personalized strategy built around your unique risk-reward profile."}
                  </Text>
                  {aiPortfolio?.indexes?.[0]?.text && (
                    <div className="bg-black/40 border border-white/5 rounded-[2rem] p-6 mt-2 text-left">
                      <Text size="sm" className="text-teal-200 uppercase tracking-[0.2em] font-black mb-3 opacity-60 flex items-center gap-2">
                        <div className="size-1 bg-teal-200 rounded-full animate-pulse" />
                        AI Strategy
                      </Text>
                      <Text size="base" className="text-white/80 leading-relaxed font-medium">
                        {aiPortfolio.indexes[0].text}
                      </Text>
                    </div>
                  )}
                </div>
              </div>

              {/* Detailed Stock Breakdown */}
              <div className="flex flex-col gap-6 pt-8 border-t border-white/5">
                <div className="flex items-center justify-between px-2">
                  <Text className="text-white/80 uppercase tracking-widest font-black text-sm flex items-center gap-2">
                    <div className="size-1.5 bg-teal-200 rounded-full" />
                    Portfolio Components
                  </Text>
                  <Text className="text-white/40 uppercase tracking-widest text-xs font-bold">{blueprintAllStocks.length} Assets</Text>
                </div>

                <div className="bg-black/20 rounded-[2rem] border border-white/5 overflow-hidden">
                  <ul className="flex flex-col">
                    {blueprintAllStocks.map((stock, i) => (
                      <li key={i} className="border-b border-white/5 last:border-0">
                        <BlueprintStockItem
                          stock={stock}
                          color={PIE_COLORS[i % PIE_COLORS.length]}
                          onSelect={setSelectedStock}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Invest in Portfolio CTA */}
              <div className="pt-6 border-t border-white/5">
                <Button
                  type={ButtonType.Primary}
                  className="w-full h-14 rounded-2xl font-bold uppercase tracking-widest bg-teal-200 text-black hover:bg-teal-300 gap-3 shadow-lg shadow-teal-200/10"
                  onClick={() => {
                    openPortfolioInvestModal(
                      blueprintAllStocks.map((s: any) => ({
                        ticker: s.ticker,
                        percentage: s.percentage,
                      })),
                      aiPortfolio?.info?.name || `${profile.name} Blueprint`,
                      aiPortfolio?.info?.description || `AI-generated portfolio for ${profile.name} personality type`,
                      BLOOMIE_IMAGES[profile.id],
                      'blueprint_invest_portfolio'
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faMoneyBillTransfer as IconProp} className="size-5" />
                  Invest in this Portfolio
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center gap-6 py-12">
              <div className="size-16 rounded-full bg-white/5 flex items-center justify-center animate-pulse">
                <FontAwesomeIcon icon={faRocket as IconProp} className="size-8 text-white/20" />
              </div>
              <div className="flex flex-col items-center gap-2">
                <Text className="text-white/60 font-bold">AI Engine Calculating...</Text>
                <Text size="sm" className="text-white/40 max-w-xs text-center">
                  Your custom portfolio is being generated. Check the browser console for details.
                </Text>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex flex-col gap-4 pt-12 border-t border-white/5 px-4 md:px-0">
        <div className="flex gap-4">
          <Button
            type={ButtonType.Secondary}
            onClick={onRestart}
            className="flex-1 gap-2 h-14 rounded-2xl"
          >
            <FontAwesomeIcon icon={faRotateLeft as IconProp} className="size-4" />
            Retake Quiz
          </Button>
          {onReplayWrapped && (
            <Button
              onClick={onReplayWrapped}
              className="flex-1 gap-2 h-14 rounded-2xl bg-teal-200 text-black hover:bg-teal-300"
            >
              <FontAwesomeIcon icon={faPlay as IconProp} className="size-4" />
              Replay Story
            </Button>
          )}
          <Button className="flex-1 gap-2 h-14 rounded-2xl bg-teal-200 text-black hover:bg-teal-300" onClick={() => setShowShareModal(true)}>
            <FontAwesomeIcon icon={faShareNodes as IconProp} className="size-4" />
            Share Results
          </Button>
        </div>
      </div>
      <div className="h-28 md:h-28" />

      {/* Stock Detail Modal */}
      {selectedStock && (
        <StockDetailModal
          symbol={selectedStock}
          onClose={() => setSelectedStock(null)}
          requireTradingOrShowGetAppModal={requireTradingOrShowGetAppModal}
        />
      )}

      {/* Portfolio Invest Modal */}
      <PortfolioInvestModal
        open={portfolioInvestModal.open}
        closeModal={closePortfolioInvestModal}
        stocks={portfolioInvestModal.stocks}
        portfolioName={portfolioInvestModal.name}
        portfolioDescription={portfolioInvestModal.description}
        portfolioImage={portfolioInvestModal.image}
      />

      <YourTypeGetAppModal
        open={getAppModalOpen}
        closeModal={closeGetAppModal}
        source={getAppModalSource}
      />

      {!getAppModalOpen && (
        <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] md:pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-3 bg-gradient-to-t from-[#090d17] via-[#090d17]/90 to-transparent backdrop-blur-sm">
          <div className="max-w-2xl mx-auto">
            <Button
              type={ButtonType.Primary}
              className="w-full h-14"
              onClick={() => {
                Analytics.track(YourTypeEvents.CTA_CLICK, {
                  source: 'sticky_start_investing',
                  canShowTradingFeatures: !!user?.canShowTradingFeatures,
                  ...baseEventProps,
                });
                setGetAppModalSource('sticky_start_investing');
                setGetAppModalOpen(true);
              }}
            >
              Start investing
            </Button>
          </div>
        </div>
      )}

      {/* Share Results Modal */}
      <ShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        profile={profile}
        recommendations={recommendations}
        aiPortfolio={aiPortfolio}
        blueprintPieData={blueprintPieData}
        blueprintAllStocks={blueprintAllStocks}
      />
    </div>
  );
};

export default YourTypeResults;

